import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { AgingChart, MonthlyChart, PageHead, Stat, StatBand, StatusBadge } from '../components/bits.tsx';
import { Button, EmptyState, Panel, TableSkeleton } from '../components/ui/index.tsx';
import { api } from '../lib/api.ts';
import { formatDate, formatMoney, formatMoneyCompact, relativeDays } from '../lib/money.ts';
import { useSession } from '../state/session.tsx';
import type {
  AgingBucket,
  DashboardSummary,
  DocumentListItem,
  MonthlyPoint,
  PartyTotal,
} from '../lib/types.ts';

interface DashboardResponse {
  range: { from: string; to: string };
  summary: DashboardSummary;
  series: MonthlyPoint[];
  aging: AgingBucket[];
  topCustomers: PartyTotal[];
  recentInvoices: DocumentListItem[];
}

export function Dashboard() {
  const { business, canWrite } = useSession();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', business?.id],
    queryFn: () => api.get<DashboardResponse>('/api/reports/dashboard'),
    enabled: Boolean(business),
  });

  const currency = data?.summary.currency ?? business?.currency ?? 'INR';

  return (
    <>
      <PageHead
        title={business?.name ?? 'Overview'}
        lede={
          data
            ? `Financial year ${formatDate(data.range.from)} — ${formatDate(data.range.to)}`
            : undefined
        }
        actions={
          canWrite && (
            <>
              <Button onClick={() => navigate('/documents/new?type=quotation')}>New quotation</Button>
              <Button variant="primary" onClick={() => navigate('/documents/new?type=invoice')}>
                New invoice
              </Button>
            </>
          )
        }
      />

      {error && (
        <Panel>
          <EmptyState title="Could not load the overview" lede={(error as Error).message} />
        </Panel>
      )}

      {isLoading && <TableSkeleton rows={4} columns={4} />}

      {data && (
        <div className="stack stack-5">
          <StatBand>
            <Stat
              lead
              label="Outstanding"
              value={formatMoneyCompact(data.summary.outstandingMinor, currency)}
              note={
                data.summary.overdueMinor > 0
                  ? `${formatMoneyCompact(data.summary.overdueMinor, currency)} overdue across ${data.summary.overdueCount}`
                  : 'Nothing overdue'
              }
              tone={data.summary.overdueMinor > 0 ? 'bad' : undefined}
            />
            <Stat
              label="Invoiced"
              value={formatMoneyCompact(data.summary.invoicedMinor, currency)}
              note={`${data.summary.invoiceCount} invoices this year`}
            />
            <Stat
              label="Collected"
              value={formatMoneyCompact(data.summary.collectedMinor, currency)}
              note={
                data.summary.averageDaysToPay === null
                  ? 'No settled invoices yet'
                  : `Paid in ${data.summary.averageDaysToPay} days on average`
              }
              tone="ok"
            />
            <Stat
              label="Expenses"
              value={formatMoneyCompact(data.summary.expensesMinor, currency)}
              note={`Net ${formatMoneyCompact(data.summary.netMinor, currency)}`}
            />
            <Stat
              label="Tax collected"
              value={formatMoneyCompact(data.summary.taxCollectedMinor, currency)}
              note="Output tax on issued invoices"
            />
          </StatBand>

          <div className="split">
            <Panel title="Revenue and expenses">
              {data.series.length === 0 ? (
                <EmptyState
                  mark="◴"
                  title="No activity yet"
                  lede="Issue your first invoice and this will start filling in."
                />
              ) : (
                <MonthlyChart series={data.series} currency={currency} />
              )}
            </Panel>

            <Panel title="Receivables ageing">
              <AgingChart buckets={data.aging} currency={currency} />
              <p className="faint" style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-xs)' }}>
                Measured from each invoice's due date.{' '}
                <Link to="/reports/receivables" style={{ color: 'var(--accent)' }}>
                  Full report
                </Link>
              </p>
            </Panel>
          </div>

          <div className="split">
            <Panel
              title="Recent invoices"
              flush
              actions={
                <Link to="/invoices" className="btn btn--ghost btn--sm">
                  View all
                </Link>
              }
            >
              {data.recentInvoices.length === 0 ? (
                <EmptyState
                  mark="▤"
                  title="No invoices yet"
                  lede="Your issued invoices will appear here."
                  action={
                    canWrite ? (
                      <Button variant="primary" onClick={() => navigate('/documents/new?type=invoice')}>
                        Create the first one
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Number</th>
                        <th>Customer</th>
                        <th>Due</th>
                        <th className="num">Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentInvoices.map((invoice) => (
                        <tr
                          key={invoice.id}
                          className="row-link"
                          tabIndex={0}
                          onClick={() => navigate(`/documents/${invoice.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') navigate(`/documents/${invoice.id}`);
                          }}
                        >
                          <td className="strong">{invoice.number}</td>
                          <td className="truncate">{invoice.partyName ?? '—'}</td>
                          <td className="muted">
                            {invoice.dueDate ? relativeDays(invoice.dueDate) : '—'}
                          </td>
                          <td className="num">
                            {formatMoney(invoice.balanceMinor, invoice.currency)}
                          </td>
                          <td>
                            <StatusBadge status={invoice.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel title="Top customers" flush>
              {data.topCustomers.length === 0 ? (
                <EmptyState mark="◍" title="No customers yet" />
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th className="num">Invoiced</th>
                        <th className="num">Owed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topCustomers.map((customer) => (
                        <tr key={customer.partyId || customer.partyName}>
                          <td className="truncate">
                            {customer.partyId ? (
                              <Link to={`/parties/${customer.partyId}`}>{customer.partyName}</Link>
                            ) : (
                              customer.partyName
                            )}
                          </td>
                          <td className="num">
                            {formatMoneyCompact(customer.invoicedMinor, currency)}
                          </td>
                          <td className="num">
                            {customer.outstandingMinor > 0 ? (
                              <span style={{ color: 'var(--warn)' }}>
                                {formatMoneyCompact(customer.outstandingMinor, currency)}
                              </span>
                            ) : (
                              <span className="faint">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          {(data.summary.draftCount > 0 || data.summary.openQuotationsCount > 0) && (
            <Panel>
              <div className="row row--wrap" style={{ gap: 'var(--space-6)' }}>
                {data.summary.draftCount > 0 && (
                  <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                    <strong style={{ color: 'var(--ink)' }}>{data.summary.draftCount}</strong> draft
                    {data.summary.draftCount === 1 ? '' : 's'} not yet issued.{' '}
                    <Link to="/invoices?status=draft" style={{ color: 'var(--accent)' }}>
                      Review
                    </Link>
                  </p>
                )}
                {data.summary.openQuotationsCount > 0 && (
                  <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                    <strong style={{ color: 'var(--ink)' }}>
                      {formatMoneyCompact(data.summary.openQuotationsMinor, currency)}
                    </strong>{' '}
                    in open quotations.{' '}
                    <Link to="/quotations" style={{ color: 'var(--accent)' }}>
                      Follow up
                    </Link>
                  </p>
                )}
              </div>
            </Panel>
          )}
        </div>
      )}
    </>
  );
}
